import React, { FC, useContext, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { Loader } from '@eika/loader';

import { AppContext } from '../../containers/appContextContainer';
import { NewSoeknadWeb } from '../../generated-types';
import { useBasicSoeknad, useSaveSoeknad } from '../../hooks/useSoeknadMedlaantaker';
import { useTranslation } from '../../translations';
import { laantakerNavn } from '../../util/formatererUtil';
import AnnenEiendom, { ANNEN_EIENDOM_ID, AnnenEiendomFormData } from '../eiendom/annenEiendom';
import EiendomList, { ValgtEiendomFormData } from '../eiendom/eiendomList';
import UserMessage from '../modal/userMessage';
import PageCard from '../page/pageCard';
import PageFooter from '../page/pageFooter';

export type OekeBoliglaan = ValgtEiendomFormData & AnnenEiendomFormData;

interface Props {
    navigateTo: string;
}

const OekeBoliglaan: FC<Props> = ({ navigateTo }) => {
    const navigate = useNavigate();
    const { oekeBoliglaan } = useTranslation();
    const { soeknadstype } = useContext(AppContext);
    const { data: soeknad, isLoading: isLoadingSoeknad, isFetchedAfterMount } = useBasicSoeknad(soeknadstype);
    const { mutate: saveSoeknad, isPending: saveingSoeknad } = useSaveSoeknad(() => {
        navigate(navigateTo);
    });
    const methods = useForm<OekeBoliglaan>({
        defaultValues: { eiendom: undefined },
    });
    const { reset, watch, handleSubmit } = methods;
    const watchAll = watch();

    useEffect((): void => {
        if (soeknad?.eiendom) {
            if (soeknad.eiendom.eiendomId.length === 0) {
                soeknad.eiendom.eiendomId = ANNEN_EIENDOM_ID;
            }
            reset({
                eiendom: soeknad.eiendom,
                adresse: soeknad?.eiendom?.eiendomId === ANNEN_EIENDOM_ID ? soeknad?.eiendom?.adresse : undefined,
                postnummer: soeknad?.eiendom?.eiendomId === ANNEN_EIENDOM_ID ? soeknad?.eiendom?.postnummer : undefined,
                poststed: soeknad?.eiendom?.eiendomId === ANNEN_EIENDOM_ID ? soeknad?.eiendom?.poststed : undefined,
            });
        }
    }, [soeknad, reset]);

    const onSumbitEiendomValg = (data: OekeBoliglaan) => {
        const newSoeknad: NewSoeknadWeb = {
            ...soeknad,
            eiendom: {
                ...data.eiendom,
                id: soeknad?.eiendom?.id,
                uuid: soeknad?.eiendom?.uuid,
                adresse: data.eiendom?.eiendomId === ANNEN_EIENDOM_ID ? data.adresse : data.eiendom?.adresse,
                postnummer: data.eiendom?.eiendomId === ANNEN_EIENDOM_ID ? data.postnummer : data.eiendom?.postnummer,
                poststed: data.eiendom?.eiendomId === ANNEN_EIENDOM_ID ? data.poststed : data.eiendom?.poststed,
            },
        } as NewSoeknadWeb;
        saveSoeknad(newSoeknad);
    };

    if (isLoadingSoeknad) return <Loader className="padding-top-extra-large" />;

    return (
        <div>
            {isFetchedAfterMount && soeknad && <UserMessage soeknad={soeknad} />}
            <PageCard title={oekeBoliglaan.title}>
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(onSumbitEiendomValg)} className="row row-gap-large">
                        <span className="typography-ingress__light">
                            <span>{`${oekeBoliglaan.formTitle} ${laantakerNavn(soeknad?.laantaker)}`}</span>
                            <br />
                            <span>{oekeBoliglaan.formSubTitle}</span>
                        </span>
                        <EiendomList />
                        {watchAll.eiendom?.eiendomId === ANNEN_EIENDOM_ID && <AnnenEiendom />}
                        <PageFooter soeknadId={soeknad?.soeknadId} showSpinner={saveingSoeknad} startPage />
                    </form>
                </FormProvider>
            </PageCard>
        </div>
    );
};

export default OekeBoliglaan;
